import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// ---------------------------------------------------------------------------
// CheckInFlow
//
// The student-facing rating flow. Walks the student sequentially through
// each of their active goals, then optionally through a
// Zones-of-Regulation feeling check. Every rating is saved as a real
// goal_progress_entries row via `logProgress`, with source='student_self'
// and student_rating set to 'met' / 'partial' / 'not_met'.
//
// Design choices:
//   * One goal per screen. A young student can only cognitively hold
//     one question at a time; a scrolling checklist is a cognitive
//     load trap for this population.
//   * The three rating buttons use color + emoji + short plain-
//     English label — three cues so a non-reader can still respond.
//   * Progress dots at the top so the student can see how close they
//     are to being done ("only two more!").
//   * "Skip this one" is available on every goal so a student who
//     genuinely didn't work on that goal today isn't forced to lie.
//   * A "Back" arrow lets the student change their mind about the
//     previous rating without losing progress.
// ---------------------------------------------------------------------------

// Rating vocabulary. The `value` maps into goal_progress_entries.value
// on a 0..100 scale with the entry-level target of 100, so the DB
// trigger naturally computes progress_percent = value directly.
const RATINGS = [
  {
    key: 'met',
    label: 'I did it!',
    emoji: '😃',
    icon: 'ThumbsUp',
    value: 100,
    // Bright, celebratory green.
    className:
      'bg-emerald-500 hover:bg-emerald-600 focus:ring-emerald-300 text-white',
    activeClassName:
      'ring-4 ring-emerald-300 ring-offset-2 bg-emerald-600',
    subtext: 'Yes, most of the time',
  },
  {
    key: 'partial',
    label: 'Kind of',
    emoji: '🙂',
    icon: 'Circle',
    value: 50,
    className:
      'bg-amber-400 hover:bg-amber-500 focus:ring-amber-300 text-white',
    activeClassName:
      'ring-4 ring-amber-300 ring-offset-2 bg-amber-500',
    subtext: 'Some of the time',
  },
  {
    key: 'not_met',
    label: 'Not today',
    emoji: '😐',
    icon: 'CloudRain',
    // Deliberately 0 (not e.g. 20). If the student says "not today",
    // that IS the data point — smoothing it upward for pity would
    // corrupt the trend line the IEP team relies on.
    value: 0,
    className:
      'bg-rose-500 hover:bg-rose-600 focus:ring-rose-300 text-white',
    activeClassName:
      'ring-4 ring-rose-300 ring-offset-2 bg-rose-600',
    subtext: "Didn't happen today",
  },
];

// Zones of Regulation vocabulary. Standard four-color model used
// district-wide in most special-ed programs. Kept as its own final
// step so a student who just wants to rate their goals can tap "Skip"
// on the feelings screen without penalty.
const FEELINGS = [
  { key: 'blue', label: 'Blue', subtext: 'Tired · Sad · Sick', emoji: '💧', className: 'bg-blue-500 hover:bg-blue-600 focus:ring-blue-300' },
  { key: 'green', label: 'Green', subtext: 'Ready · Calm · Happy', emoji: '🌱', className: 'bg-emerald-500 hover:bg-emerald-600 focus:ring-emerald-300' },
  { key: 'yellow', label: 'Yellow', subtext: 'Wiggly · Silly · Worried', emoji: '☀️', className: 'bg-amber-400 hover:bg-amber-500 focus:ring-amber-300' },
  { key: 'red', label: 'Red', subtext: 'Angry · Upset · Out of control', emoji: '🔥', className: 'bg-rose-500 hover:bg-rose-600 focus:ring-rose-300' },
];

function shortGoalText(goal) {
  const raw =
    (goal?.specific && goal.specific.trim()) ||
    (goal?.measurable && goal.measurable.trim()) ||
    'your goal';
  // Truncate a super-long IEP paragraph to something a kid can process
  // at a glance. Full text is still stored in the DB for the teacher.
  if (raw.length <= 140) return raw;
  return `${raw.slice(0, 137).trim()}…`;
}

const CheckInFlow = ({
  student,
  goals,
  logProgress,
  onComplete,
  onSwitchStudent,
}) => {
  const [step, setStep] = useState(0); // 0..goals.length-1 = goal, goals.length = feeling
  const [ratings, setRatings] = useState({}); // goalId -> ratingKey
  const [feeling, setFeeling] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const totalSteps = goals.length + 1; // +1 for the feeling step
  const isFeelingStep = step === goals.length;
  const currentGoal = !isFeelingStep ? goals[step] : null;

  // No goals — nothing to rate. This is a legitimate edge case
  // (student has no active IEP goals yet) so give the adult a clear
  // way out instead of a broken screen.
  if (goals.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-2xl border border-gray-200 shadow-sm">
        <Icon
          name="Target"
          size={56}
          className="mx-auto mb-4 text-gray-300"
        />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {student?.firstName || 'This student'} doesn't have any active
          goals yet.
        </h2>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Once you write an IEP goal for {student?.firstName || 'them'},
          they can check in on it here.
        </p>
        <Button variant="outline" onClick={onSwitchStudent}>
          <Icon name="ArrowLeft" size={16} className="mr-2" />
          Pick a different student
        </Button>
      </div>
    );
  }

  const setRatingAndAdvance = (goalId, ratingKey) => {
    setSaveError('');
    setRatings((prev) => ({ ...prev, [goalId]: ratingKey }));
    // Auto-advance to keep the flow moving. If the student wants to
    // change their answer, the Back button on the next screen brings
    // them right back.
    setTimeout(() => {
      setStep((s) => Math.min(s + 1, goals.length));
    }, 250);
  };

  const setFeelingAndFinish = async (feelingKey) => {
    setFeeling(feelingKey);
    await finish(feelingKey);
  };

  const finish = async (feelingKey) => {
    setSaving(true);
    setSaveError('');
    const ratedGoalIds = Object.keys(ratings);
    try {
      // Save every rated goal in parallel. Skipped goals never made it
      // into `ratings`, so nothing is written for them.
      await Promise.all(
        ratedGoalIds.map((goalId) => {
          const ratingKey = ratings[goalId];
          const spec = RATINGS.find((r) => r.key === ratingKey);
          if (!spec) return Promise.resolve();
          return logProgress(goalId, {
            studentId: student?.id,
            value: spec.value,
            // Entry-level target of 100 so the DB progress trigger
            // reads value directly as a percent. Independent of
            // whatever numeric target the goal itself has — a self
            // check-in is a categorical rating, not a probe.
            targetValue: 100,
            measurementType: 'percent_correct',
            source: 'student_self',
            studentRating: ratingKey,
            feeling: feelingKey || null,
            notes: `Self check-in by ${student?.firstName || 'student'}${feelingKey ? ` — feeling ${feelingKey}` : ''}`,
          });
        }),
      );
      onComplete({
        ratedGoalIds,
        feeling: feelingKey || null,
      });
    } catch (err) {
      setSaveError(
        err?.message || "Couldn't save your check-in — try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    setSaveError('');
    setStep((s) => Math.max(0, s - 1));
  };

  const skipCurrentGoal = () => {
    if (!currentGoal) return;
    setSaveError('');
    setRatings((prev) => {
      const next = { ...prev };
      delete next[currentGoal.id];
      return next;
    });
    setStep((s) => Math.min(s + 1, goals.length));
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 text-center">
        <p className="text-sm text-gray-600 mb-1">
          Hi <span className="font-semibold text-gray-900">{student?.firstName || 'friend'}</span>!
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {isFeelingStep
            ? 'How are you feeling right now?'
            : "Let's check in on your goal"}
        </h1>
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2 mb-8" aria-hidden="true">
        {Array.from({ length: totalSteps }).map((_, i) => {
          const done = i < step;
          const current = i === step;
          return (
            <div
              key={i}
              className={`h-2 rounded-full transition-all ${
                current
                  ? 'w-8 bg-primary'
                  : done
                    ? 'w-3 bg-primary/60'
                    : 'w-3 bg-gray-200'
              }`}
            />
          );
        })}
      </div>

      {/* Card */}
      <div className="bg-white rounded-3xl border-2 border-gray-100 shadow-lg p-6 sm:p-10">
        {saveError && (
          <div
            role="alert"
            className="mb-4 rounded-xl bg-rose-50 border-2 border-rose-200 p-4 text-rose-800 text-sm"
          >
            <div className="flex items-start gap-2">
              <Icon name="AlertCircle" size={18} className="mt-0.5 flex-shrink-0" />
              <p>{saveError}</p>
            </div>
          </div>
        )}

        {!isFeelingStep ? (
          <GoalStep
            student={student}
            goal={currentGoal}
            selectedRating={ratings[currentGoal.id] || null}
            stepIndex={step}
            totalGoals={goals.length}
            onPick={(ratingKey) => setRatingAndAdvance(currentGoal.id, ratingKey)}
            onSkip={skipCurrentGoal}
            onBack={step > 0 ? goBack : null}
          />
        ) : (
          <FeelingStep
            student={student}
            saving={saving}
            onPick={setFeelingAndFinish}
            onSkip={() => finish(null)}
            onBack={goBack}
          />
        )}
      </div>

      {/* Adult-facing switch-student escape hatch. Small and tucked
          out of the primary tap zone so a kid doesn't accidentally
          reset their whole check-in. */}
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onSwitchStudent}
          className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
        >
          Not {student?.firstName || 'me'}? Pick a different student
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-step: one goal
// ---------------------------------------------------------------------------
const GoalStep = ({
  student,
  goal,
  selectedRating,
  stepIndex,
  totalGoals,
  onPick,
  onSkip,
  onBack,
}) => {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-primary font-semibold mb-2 text-center">
        Goal {stepIndex + 1} of {totalGoals}
      </p>
      <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6 text-center leading-snug">
        {shortGoalText(goal)}
      </h2>

      <div className="space-y-3">
        {RATINGS.map((r) => {
          const isSelected = selectedRating === r.key;
          return (
            <button
              key={r.key}
              type="button"
              onClick={() => onPick(r.key)}
              className={`w-full flex items-center gap-4 rounded-2xl px-5 py-5 sm:py-6 text-left shadow-md transition-all duration-150 focus:outline-none focus:ring-4 active:scale-[0.98] ${r.className} ${isSelected ? r.activeClassName : ''}`}
              aria-pressed={isSelected}
            >
              <span
                className="flex-shrink-0 text-4xl sm:text-5xl leading-none select-none"
                aria-hidden="true"
              >
                {r.emoji}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xl sm:text-2xl font-bold leading-tight">
                  {r.label}
                </p>
                <p className="text-sm text-white/85 mt-0.5">
                  {r.subtext}
                </p>
              </div>
              {isSelected && (
                <Icon
                  name="CheckCircle2"
                  size={28}
                  className="flex-shrink-0"
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex items-center justify-between text-sm">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            <Icon name="ArrowLeft" size={16} />
            Back
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={onSkip}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
        >
          Skip this one
          <Icon name="ArrowRight" size={16} />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-step: Zones of Regulation
// ---------------------------------------------------------------------------
const FeelingStep = ({ saving, onPick, onSkip, onBack }) => {
  return (
    <div>
      <p className="text-center text-gray-600 mb-6">
        Pick the one that feels most like you right now. There's no
        wrong answer.
      </p>

      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {FEELINGS.map((f) => (
          <button
            key={f.key}
            type="button"
            disabled={saving}
            onClick={() => onPick(f.key)}
            className={`flex flex-col items-center gap-2 rounded-2xl px-4 py-6 text-white shadow-md transition-all duration-150 focus:outline-none focus:ring-4 focus:ring-offset-2 active:scale-95 disabled:opacity-70 ${f.className}`}
          >
            <span
              className="text-5xl leading-none select-none"
              aria-hidden="true"
            >
              {f.emoji}
            </span>
            <p className="text-lg font-bold">{f.label}</p>
            <p className="text-xs text-white/85 text-center leading-tight">
              {f.subtext}
            </p>
          </button>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onBack}
          disabled={saving}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
        >
          <Icon name="ArrowLeft" size={16} />
          Back
        </button>
        <button
          type="button"
          onClick={onSkip}
          disabled={saving}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
        >
          {saving ? (
            <>
              <Icon name="Loader2" size={16} className="animate-spin" />
              Saving…
            </>
          ) : (
            <>
              I'd rather not say
              <Icon name="ArrowRight" size={16} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CheckInFlow;
